import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { HiOutlineChevronRight, HiOutlineMenu } from "react-icons/hi";
import { toast } from "sonner";

import DashboardLayout from "layouts/dashboard/DashboardLayout";
import InactiveStepView from "./InactiveStepView";
import {
  FollowUpWorkflowStep,
  getFollowUpDiscussionDetails,
  skipFollowUpStep,
} from "../../api/followUpDiscussion";
import FollowUpPreliminaryDiscussionView, {
  FOLLOW_UP_PRELIMINARY_FORM_ID,
} from "./FollowUpPreliminaryDiscussionView";
import FollowUpApplicationDetailsFromClientView, {
  FOLLOW_UP_APPLICATION_DETAILS_FORM_ID,
} from "./FollowUpApplicationDetailsFromClientView";
import FollowUpInvoiceAdvanceAmountView, {
  FOLLOW_UP_INVOICE_ADVANCE_FORM_ID,
} from "./FollowUpInvoiceAdvanceAmountView";
import FollowUpAdvanceAmountFromClientView, {
  FOLLOW_UP_ADVANCE_AMOUNT_FROM_CLIENT_FORM_ID,
} from "./FollowUpAdvanceAmountFromClientView";
import FollowUpAnalysisReportView, {
  FOLLOW_UP_ANALYSIS_REPORT_FORM_ID,
} from "./FollowUpAnalysisReportView";
import FollowUpLegalAdviceView, {
  FOLLOW_UP_LEGAL_ADVICE_FORM_ID,
} from "./FollowUpLegalAdviceView";
import FollowUpInvoiceToClientView, {
  FOLLOW_UP_INVOICE_TO_CLIENT_FORM_ID,
} from "./FollowUpInvoiceToClientView";
import FollowUpPaymentFromClientView, {
  FOLLOW_UP_PAYMENT_FROM_CLIENT_FORM_ID,
} from "./FollowUpPaymentFromClientView";
import FollowUpStampPaperDetailsToVendorView, {
  FOLLOW_UP_STAMP_PAPER_DETAILS_TO_VENDOR_FORM_ID,
} from "./FollowUpStampPaperDetailsToVendorView";
import FollowUpStampPaperFromVendorView, {
  FOLLOW_UP_STAMP_PAPER_FROM_VENDOR_FORM_ID,
} from "./FollowUpStampPaperFromVendorView";
import FollowUpPreparationOfPowerOfAttorneyView, {
  FOLLOW_UP_PREPARATION_OF_POA_FORM_ID,
} from "./FollowUpPreparationOfPowerOfAttorneyView";
import FollowUpFileTmMView, {
  FOLLOW_UP_FILE_TM_M_FORM_ID,
} from "./FollowUpFileTmMView";
import FollowUpInformClientTmMFiledView, {
  FOLLOW_UP_INFORM_CLIENT_TM_M_FILED_FORM_ID,
} from "./FollowUpInformClientTmMFiledView";

type Section = "discussion" | "filing" | "status";

const DISCUSSION_ITEMS: { id: string; label: string }[] = [
  { id: "preliminary", label: "Preliminary Discussion with the Client" },
  { id: "application-details", label: "Application details from Client for Follow-up" },
  { id: "invoice-advance", label: "Invoice of Advance Amount to Client" },
  { id: "advance-amount", label: "Advance Amount from Client" },
  { id: "analysis-report", label: "Analysis Report on the status of application" },
  { id: "legal-advice", label: "Legal Advice" },
  { id: "invoice-final", label: "Invoice to Client for Follow-up" },
  { id: "payment", label: "Payment from Client" },
];

const DISCUSSION_ID_TO_SLUG: Record<string, string> = {
  preliminary: "preliminary-discussion-with-the-client",
  "application-details": "application-details-from-client",
  "invoice-advance": "invoice-of-advance-amount-to-client",
  "advance-amount": "advance-amount-from-client",
  "analysis-report": "analysis-report-on-the-status-of-application",
  "legal-advice": "legal-advice",
  "invoice-final": "invoice-to-client-for-follow-up",
  payment: "payment-from-client",
};

const DISCUSSION_SLUG_TO_ID: Record<string, string> = Object.fromEntries(
  Object.entries(DISCUSSION_ID_TO_SLUG).map(([k, v]) => [v, k])
);

const FILING_ITEMS: { id: string; label: string }[] = [
  { id: "stamp-paper-details-to-vendor", label: "Stamp Paper details to Vendor" },
  { id: "stamp-paper-from-vendor", label: "Stamp Paper from Vendor" },
  {
    id: "preparation-of-power-of-attorney",
    label: "Preparation of Power of Attorney",
  },
  { id: "file-tm-m", label: "File TM-M for Follow-up" },
  {
    id: "inform-client-tm-m-filed",
    label: "Inform Client about Filing of TM-M for Follow-up",
  },
];

const FILING_ID_TO_SLUG: Record<string, string> = {
  "stamp-paper-details-to-vendor": "stamp-paper-details-to-vendor",
  "stamp-paper-from-vendor": "stamp-paper-from-vendor",
  "preparation-of-power-of-attorney": "preparation-of-power-of-attorney",
  "file-tm-m": "file-tm-m",
  "inform-client-tm-m-filed": "inform-client-tm-m-filed",
};

const FILING_SLUG_TO_ID: Record<string, string> = Object.fromEntries(
  Object.entries(FILING_ID_TO_SLUG).map(([k, v]) => [v, k])
);

const STATUS_ITEMS: { id: string; label: string }[] = [];

export default function DashboardFollowUp() {
  const navigate = useNavigate();
  const { section, step, leadId } = useParams<{
    section?: string;
    step?: string;
    leadId?: string;
  }>();

  const [menuOpen, setMenuOpen] = useState(false);
  const [markNameForHeader, setMarkNameForHeader] = useState<string | null>(null);
  const [invoiceAdvanceSent, setInvoiceAdvanceSent] = useState(false);
  const [invoiceFinalSent, setInvoiceFinalSent] = useState(false);
  const [invoiceFinalBusy, setInvoiceFinalBusy] = useState(false);
  const [stampPaperDetailsSent, setStampPaperDetailsSent] = useState(false);
  const [informClientTmMSent, setInformClientTmMSent] = useState(false);
  const [progressing, setProgressing] = useState(false);

  const activeTab: Section =
    section === "filing" ? "filing" : section === "status" ? "status" : "discussion";
  const activeMenuItem =
    activeTab === "discussion" && step
      ? (DISCUSSION_SLUG_TO_ID[step] ?? "preliminary")
      : activeTab === "filing" && step
        ? (FILING_SLUG_TO_ID[step] ?? "stamp-paper-details-to-vendor")
        : activeTab === "filing"
          ? "stamp-paper-details-to-vendor"
          : "preliminary";

  useEffect(() => {
    if (!leadId) {
      setMarkNameForHeader(null);
      return;
    }
    let cancelled = false;
    getFollowUpDiscussionDetails(leadId)
      .then((details) => {
        if (!cancelled) setMarkNameForHeader(details.preliminary.markName ?? null);
      })
      .catch(() => {
        if (!cancelled) setMarkNameForHeader(null);
      });
    return () => { cancelled = true; };
  }, [leadId]);

  useEffect(() => {
    if (!section || !step) {
      navigate(
        "/dashboard/follow-ups/new/discussion/preliminary-discussion-with-the-client",
        { replace: true }
      );
    }
  }, [section, step, navigate]);

  const navigateToStep = (
    tab: Section,
    itemId: string,
    currentLeadId?: string | null
  ) => {
    const slug =
      tab === "discussion"
        ? DISCUSSION_ID_TO_SLUG[itemId]
        : tab === "filing"
          ? FILING_ID_TO_SLUG[itemId]
          : null;
    if (!slug) return;
    const base = `/dashboard/follow-ups/new/${tab}/${slug}`;
    const path = currentLeadId ? `${base}/${currentLeadId}` : base;
    navigate(path);
    setMenuOpen(false);
  };

  const handleTabChange = (tab: Section) => {
    if (tab === "status") return;
    const firstStep =
      tab === "filing"
        ? "stamp-paper-details-to-vendor"
        : "preliminary-discussion-with-the-client";
    const path = leadId
      ? `/dashboard/follow-ups/new/${tab}/${firstStep}/${leadId}`
      : `/dashboard/follow-ups/new/${tab}/${firstStep}`;
    navigate(path);
  };

  const leftPanelItems =
    activeTab === "filing"
      ? FILING_ITEMS
      : activeTab === "status"
        ? STATUS_ITEMS
        : DISCUSSION_ITEMS;
  const activeLeftId =
    activeTab === "discussion" || activeTab === "filing" ? activeMenuItem : "";

  const items = leftPanelItems;
  const activeIdx = items.findIndex((m) => m.id === activeLeftId);
  const canGoBack = activeIdx > 0;
  const FORM_MAP: Record<string, string | undefined> = {
    "preliminary": FOLLOW_UP_PRELIMINARY_FORM_ID,
    "application-details": FOLLOW_UP_APPLICATION_DETAILS_FORM_ID,
    "invoice-advance": FOLLOW_UP_INVOICE_ADVANCE_FORM_ID,
    "advance-amount": FOLLOW_UP_ADVANCE_AMOUNT_FROM_CLIENT_FORM_ID,
    "analysis-report": FOLLOW_UP_ANALYSIS_REPORT_FORM_ID,
    "legal-advice": FOLLOW_UP_LEGAL_ADVICE_FORM_ID,
    "invoice-final": FOLLOW_UP_INVOICE_TO_CLIENT_FORM_ID,
    "payment": FOLLOW_UP_PAYMENT_FROM_CLIENT_FORM_ID,
    "stamp-paper-details-to-vendor":
      FOLLOW_UP_STAMP_PAPER_DETAILS_TO_VENDOR_FORM_ID,
    "stamp-paper-from-vendor": FOLLOW_UP_STAMP_PAPER_FROM_VENDOR_FORM_ID,
    "preparation-of-power-of-attorney":
      FOLLOW_UP_PREPARATION_OF_POA_FORM_ID,
    "file-tm-m": FOLLOW_UP_FILE_TM_M_FORM_ID,
    "inform-client-tm-m-filed": FOLLOW_UP_INFORM_CLIENT_TM_M_FILED_FORM_ID,
  };

  const submitForm =
    activeTab === "discussion" || activeTab === "filing"
      ? FORM_MAP[activeMenuItem] || null
      : null;

  const isInvoiceAdvance =
  activeTab === "discussion" && activeMenuItem === "invoice-advance";
  const isInvoiceFinal =
  activeTab === "discussion" && activeMenuItem === "invoice-final";
  const isStampPaperDetailsToVendor =
    activeTab === "filing" && activeMenuItem === "stamp-paper-details-to-vendor";
  const isInformClientTmMFiled =
    activeTab === "filing" && activeMenuItem === "inform-client-tm-m-filed";

  const primaryActionLabel =
  (isInvoiceAdvance && !invoiceAdvanceSent) ||
  (isInvoiceFinal && !invoiceFinalSent) ||
  (isStampPaperDetailsToVendor && !stampPaperDetailsSent) ||
  (isInformClientTmMFiled && !informClientTmMSent)
    ? "Send & Continue"
    : "Save & Continue";
      

  const progressStep: {
    step: string;
    nextTab?: Section;
    nextItemId?: string;
  } | null =
    activeTab === "discussion" &&
    activeMenuItem === "invoice-advance" &&
    !invoiceAdvanceSent
      ? {
          step: FollowUpWorkflowStep.FU_S1_03_InvoiceOfAdvanceAmountToClient,
          nextItemId: "advance-amount",
        }
      : activeTab === "discussion" && activeMenuItem === "advance-amount"
        ? {
            step: FollowUpWorkflowStep.FU_S1_04_AdvanceAmountFromClientForFollowUp,
            nextItemId: "analysis-report",
          }
        : activeTab === "discussion" && activeMenuItem === "payment"
          ? {
              step: FollowUpWorkflowStep.FU_S1_08_PaymentFromClientForFollowUp,
              nextTab: "filing",
              nextItemId: "stamp-paper-details-to-vendor",
            }
          : null;

  const handleProgress = async () => {
    if (!progressStep || !leadId || progressing) return;
    setProgressing(true);
    try {
      await skipFollowUpStep(leadId, progressStep.step);
      toast.success("Step marked as skipped.");
      if (progressStep.nextItemId) {
        navigateToStep(
          progressStep.nextTab ?? activeTab,
          progressStep.nextItemId,
          leadId
        );
      } else {
        navigate("/dashboard/follow-ups");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to skip step");
    } finally {
      setProgressing(false);
    }
  };

  return (
    <DashboardLayout headerTitle="Follow Up" headerLeadNo={markNameForHeader ?? undefined} hideHeaderOnMobile>
      <div className="flex-1 p-2 min-h-0 flex flex-col min-w-0">
        <div className="bg-white rounded-[8px] h-full flex flex-col overflow-hidden">
          {/* Tab bar */}
          <div className="flex items-center gap-0 border-b border-[#EEEEEE] pl-[10px] pt-[6px] flex-shrink-0">
            <button
              onClick={() => handleTabChange("discussion")}
              className={`px-2.5 py-2.5 text-[14px] leading-5 -mb-px border-b-2 transition-colors ${
                activeTab === "discussion"
                  ? "font-semibold text-[#222] border-[#222]"
                  : "font-normal text-[#222] border-transparent"
              }`}
            >
              Discussion
            </button>
            <button
              onClick={() => handleTabChange("filing")}
              className={`px-2.5 py-2.5 text-[14px] leading-5 -mb-px border-b-2 transition-colors ${
                activeTab === "filing"
                  ? "font-semibold text-[#222] border-[#222]"
                  : "font-normal text-[#222] border-transparent"
              }`}
            >
              Filing
            </button>
            <button
              onClick={() => handleTabChange("status")}
              className={`px-2.5 py-2.5 text-[14px] leading-5 -mb-px border-b-2 transition-colors ${
                activeTab === "status"
                  ? "font-semibold text-[#222] border-[#222]"
                  : "font-normal text-[#222] border-transparent"
              }`}
            >
              Status
            </button>
          </div>

          {/* Mobile-only header: hamburger + "Follow Up" */}
          <div className="md:hidden flex items-center gap-3 h-[65px] px-4 border-b border-[#E5E7EB] flex-shrink-0 pb-[0.833px]">
            <button
              type="button"
              aria-label="Open menu"
              onClick={() => setMenuOpen(true)}
              className="flex items-center justify-center rounded-[10px] w-9 h-9 min-w-[36px] min-h-[36px] hover:bg-[#F7F7F7]"
            >
              <HiOutlineMenu className="w-5 h-5 text-[#222]" strokeWidth={1.5} />
            </button>
            <div className="flex-1 min-w-0 font-medium text-[14px] leading-5 text-[#242424] truncate">
              Follow Up{markNameForHeader ? ` | ${markNameForHeader}` : ""}
            </div>
          </div>

          <div className="flex flex-1 overflow-hidden relative">
            {menuOpen && (
              <div
                className="absolute inset-0 bg-black/20 z-40 md:hidden"
                onClick={() => setMenuOpen(false)}
              />
            )}

            <div
              className={`w-[279px] border-r border-[#EEEEEE] bg-white p-2.5 overflow-y-auto flex-shrink-0 ${
                menuOpen
                  ? "absolute left-0 top-0 bottom-0 z-50 md:relative"
                  : "hidden md:block"
              }`}
            >
              <div className="flex flex-col gap-2">
                {leftPanelItems.length === 0 ? (
                  <div className="text-xs text-[#6D6D6D] px-2.5 py-2">
                    No steps available yet.
                  </div>
                ) : (
                  leftPanelItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() =>
                        navigateToStep(activeTab, item.id, leadId ?? null)
                      }
                      className={`w-full flex items-center justify-between px-2.5 py-2.5 gap-2.5 rounded-[6px] text-xs ${
                        activeLeftId === item.id
                          ? "bg-[#222222] text-white font-medium"
                          : "bg-[#F7F7F7] text-[#222222] font-normal"
                      } hover:bg-opacity-90 transition-colors`}
                    >
                      <span className="text-left line-clamp-1">{item.label}</span>
                      <HiOutlineChevronRight
                        className="w-4 h-4 flex-shrink-0 ml-1"
                        strokeWidth={1.5}
                      />
                    </button>
                  ))
                )}
              </div>
            </div>

            <div className="flex-1 flex flex-col min-w-0">
              <div className="flex-1 p-5 md:p-6 overflow-y-auto">
                {activeTab === "discussion" && activeMenuItem === "preliminary" ? (
                  <FollowUpPreliminaryDiscussionView
                    leadId={leadId ?? undefined}
                    onSuccess={(newLeadId) => {
                      navigateToStep(
                        "discussion",
                        "application-details",
                        newLeadId
                      );
                    }}
                  />
                ) : activeTab === "discussion" &&
                  activeMenuItem === "application-details" ? (
                  <FollowUpApplicationDetailsFromClientView
                    leadId={leadId ?? null}
                    onSuccess={(currentLeadId) => {
                      navigateToStep(
                        "discussion",
                        "invoice-advance",
                        currentLeadId
                      );
                    }}
                  />
                ) : activeTab === "discussion" &&
                  activeMenuItem === "invoice-advance" ? (
                  <FollowUpInvoiceAdvanceAmountView
                    leadId={leadId ?? null}
                    onSentStatusChange={setInvoiceAdvanceSent}
                    onSuccess={(currentLeadId) => {
                      navigateToStep(
                        "discussion",
                        "advance-amount",
                        currentLeadId
                      );
                    }}
                  />
                ) : activeTab === "discussion" &&
                  activeMenuItem === "advance-amount" ? (
                  <FollowUpAdvanceAmountFromClientView
                    leadId={leadId ?? null}
                    onSuccess={(currentLeadId) => {
                      navigateToStep(
                        "discussion",
                        "analysis-report",
                        currentLeadId
                      );
                    }}
                  />
                ) : activeTab === "discussion" &&
                  activeMenuItem === "analysis-report" ? (
                  <FollowUpAnalysisReportView
                    leadId={leadId ?? null}
                    onSuccess={(currentLeadId) => {
                      navigateToStep(
                        "discussion",
                        "legal-advice",
                        currentLeadId
                      );
                    }}
                  />
                ) : activeTab === "discussion" &&
                  activeMenuItem === "legal-advice" ? (
                  <FollowUpLegalAdviceView
                    leadId={leadId ?? null}
                    onSuccess={(currentLeadId, clientGoingAhead) => {
                      if (clientGoingAhead) {
                        navigateToStep(
                          "discussion",
                          "invoice-final",
                          currentLeadId
                        );
                      } else {
                        navigate("/dashboard/follow-ups");
                      }
                    }}
                  />
                ) : activeTab === "discussion" &&
                  activeMenuItem === "invoice-final" ? (
                  <FollowUpInvoiceToClientView
                    leadId={leadId ?? null}
                    onSentStatusChange={setInvoiceFinalSent}
                    onBusyChange={setInvoiceFinalBusy}
                    onSuccess={(currentLeadId) => {
                      navigateToStep(
                        "discussion",
                        "payment",
                        currentLeadId
                      );
                    }}
                  />
                ) : activeTab === "discussion" &&
                  activeMenuItem === "payment" ? (
                  <FollowUpPaymentFromClientView
                    leadId={leadId ?? null}
                    onSuccess={(currentLeadId) => {
                      navigateToStep(
                        "filing",
                        "stamp-paper-details-to-vendor",
                        currentLeadId ?? leadId ?? null
                      );
                    }}
                  />
                ) : activeTab === "filing" &&
                  activeMenuItem === "stamp-paper-details-to-vendor" ? (
                  <FollowUpStampPaperDetailsToVendorView
                    leadId={leadId ?? null}
                    onSentStatusChange={setStampPaperDetailsSent}
                    onSuccess={(currentLeadId) => {
                      navigateToStep(
                        "filing",
                        "stamp-paper-from-vendor",
                        currentLeadId
                      );
                    }}
                  />
                ) : activeTab === "filing" &&
                  activeMenuItem === "stamp-paper-from-vendor" ? (
                  <FollowUpStampPaperFromVendorView
                    leadId={leadId ?? null}
                    onSuccess={(currentLeadId) => {
                      navigateToStep(
                        "filing",
                        "preparation-of-power-of-attorney",
                        currentLeadId
                      );
                    }}
                  />
                ) : activeTab === "filing" &&
                  activeMenuItem === "preparation-of-power-of-attorney" ? (
                  <FollowUpPreparationOfPowerOfAttorneyView
                    leadId={leadId ?? null}
                    onSuccess={(currentLeadId) => {
                      navigateToStep(
                        "filing",
                        "file-tm-m",
                        currentLeadId
                      );
                    }}
                  />
                ) : activeTab === "filing" &&
                  activeMenuItem === "file-tm-m" ? (
                  <FollowUpFileTmMView
                    leadId={leadId ?? null}
                    onSuccess={(currentLeadId) => {
                      navigateToStep(
                        "filing",
                        "inform-client-tm-m-filed",
                        currentLeadId
                      );
                    }}
                  />
                ) : activeTab === "filing" &&
                  activeMenuItem === "inform-client-tm-m-filed" ? (
                  <FollowUpInformClientTmMFiledView
                    leadId={leadId ?? null}
                    onSentStatusChange={setInformClientTmMSent}
                    onSuccess={() => {
                      navigate("/dashboard/follow-ups");
                    }}
                  />
                ) : (
                  <InactiveStepView
                    title="Step coming soon"
                    body="This step has not been implemented yet. Please continue with the available steps."
                    buttonText="Back to Preliminary Discussion"
                    onButtonClick={() =>
                      navigateToStep("discussion", "preliminary", leadId ?? null)
                    }
                  />
                )}
              </div>

              {/* Footer */}
              <div className="border-t border-[#EEE] p-3 flex items-center justify-between gap-2 flex-shrink-0">
                <button
                  type="button"
                  disabled={!canGoBack}
                  onClick={() => {
                    if (!canGoBack) return;
                    navigateToStep(activeTab, items[activeIdx - 1].id, leadId ?? null);
                  }}
                  className="px-4 py-2 h-9 text-[12px] font-semibold text-[#6D6D6D] border border-[#EEE] rounded-[6px] bg-white hover:bg-[#F7F7F7] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Back
                </button>
                <div className="flex items-center gap-2 sm:gap-3 md:gap-5">
                  <button
                    type="button"
                    onClick={() => navigate("/dashboard/follow-ups")}
                    className="px-4 py-2 h-9 text-[12px] font-semibold text-[#6D6D6D] border border-[#EEE] rounded-[6px] bg-white hover:bg-[#F7F7F7]"
                  >
                    Close
                  </button>
                  {progressStep && (
                    <button
                      type="button"
                      onClick={handleProgress}
                      disabled={progressing}
                      className="px-4 py-2 h-9 text-[12px] font-semibold text-[#222] border border-[#222] rounded-[6px] bg-white hover:bg-[#F7F7F7] disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {progressing ? "Progressing…" : "Progress"}
                    </button>
                  )}
                  <button
                    type={submitForm ? "submit" : "button"}
                    form={submitForm ?? undefined}
                    disabled={!submitForm || (isInvoiceFinal && invoiceFinalBusy)}
                    className="px-4 py-2 h-9 min-w-[128px] text-[12px] font-semibold text-white rounded-[6px] bg-[#222] hover:bg-[#333] disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isInvoiceFinal && invoiceFinalBusy
                      ? "Sending…"
                      : primaryActionLabel}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
